import { Radio, Stack, Typography } from '@mui/material';
import { compact, sortBy } from 'lodash';
import { first } from 'lodash';
import { useState } from 'react';

import { Accordion } from '@/components/Accordion/Accordion';
import { usePresentationStringFormatter } from '@/modules/presentation/clientPresentation/presentationString/usePresentationStringFormatter';
import {
  getTemplateBundleLabel,
  getTemplateBundleLabelPageInput,
} from '@/modules/presentationTemplates/PresentationTemplateEditor/stages/presentationTemplate/PresentationTemplateStageEditor.utils';
import { COLORS } from '@/styles/tokens/colors';
import { getNodes } from '@/utils/graphqlUtils';

import { getPresentationTemplateName } from '../presentationTemplate.utils';
import { PresentationTemplateSelection_PresentationTemplateFragment } from './graphql/PresentationTemplateSelection.generated';

type BundleNode = NonNullable<
  NonNullable<
    PresentationTemplateSelection_PresentationTemplateFragment['bundles']['edges']
  >[number]
>['node'];

function getBundleLabel(bundle: NonNullable<BundleNode>): string {
  const firstPage = first(getNodes(bundle.pages));
  return getTemplateBundleLabel({
    kind: bundle.kind,
    ...getTemplateBundleLabelPageInput(firstPage),
    documentSummaryTemplateBundleConfiguration:
      bundle.documentSummaryTemplateBundleConfiguration,
  });
}

interface PresentationTemplateSelectionModalDisplayItemProps {
  template: PresentationTemplateSelection_PresentationTemplateFragment;
  isSelected: boolean;
  isLastInSection: boolean;
  onClick: () => void;
}

export function PresentationTemplateSelectionModalDisplayItem({
  template,
  isSelected,
  isLastInSection,
  onClick,
}: PresentationTemplateSelectionModalDisplayItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { plainFormatter } = usePresentationStringFormatter();

  const bundles = compact(
    template.bundles.edges?.map((edge) => edge?.node) ?? []
  );
  const sortedBundles = sortBy(bundles, 'index');

  // Split bundles into two columns that fill vertically first
  const midpoint = Math.ceil(sortedBundles.length / 2);
  const leftColumn = sortedBundles.slice(0, midpoint);
  const rightColumn = sortedBundles.slice(midpoint);

  return (
    <Accordion
      variant="filled"
      withDivider={false}
      sx={{
        border: 'none !important',
        // Show the interior divider unless this is the last item in the section and collapsed
        '.MuiAccordionSummary-root': {
          borderBottom:
            !isLastInSection || isExpanded
              ? `1px solid ${COLORS.GRAY[300]}`
              : 'none',
          pb: 1.5,
          mb: 0,
        },
      }}
      onChange={(expanded) => {
        setIsExpanded(expanded);
        onClick();
      }}
      leftAdornment={
        // Radio is display-only; clicks bubble to the Accordion's onChange
        // which handles both selection and expansion
        <Radio checked={isSelected} readOnly sx={{ p: 0 }} />
      }
      labelContainerProps={{
        sx: { cursor: 'pointer' },
      }}
      rightAdornment={
        <Typography variant="body-xs" sx={{ whiteSpace: 'nowrap' }}>
          {isExpanded ? 'Hide details' : 'Show details'}
        </Typography>
      }
      title={getPresentationTemplateName(template)}
    >
      <Stack direction="row">
        <Stack flex="0 0 50%" spacing={1.5}>
          {leftColumn.map((bundle) => (
            <Typography key={bundle.id} variant="body-xs">
              {plainFormatter(getBundleLabel(bundle))}
            </Typography>
          ))}
        </Stack>
        <Stack flex="0 0 50%" spacing={1.5}>
          {rightColumn.map((bundle) => (
            <Typography key={bundle.id} variant="body-xs">
              {plainFormatter(getBundleLabel(bundle))}
            </Typography>
          ))}
        </Stack>
      </Stack>
    </Accordion>
  );
}
